import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { CRMStatCard } from '@/modules/crm/components/CRMStatCard';
import { toast } from 'sonner';
import {
  Users,
  TrendingUp,
  Target,
  DollarSign,
  Calendar,
  Phone,
  Mail,
  CheckSquare,
  ArrowRight,
  Clock,
  UserPlus,
  FileText,
  Handshake,
  RefreshCw } from
'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer } from
'recharts';

const API_URL = API_BASE || (
  typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)
    ? 'http://localhost:8001'
    : ''
);

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const readDashboardError = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  const body = await response.text();

  if (contentType.includes('application/json')) {
    try {
      const data = JSON.parse(body);
      return data.detail || data.message || `CRM dashboard request failed (${response.status})`;
    } catch {
      return `CRM dashboard returned invalid JSON (${response.status})`;
    }
  }

  const trimmed = body.trim().toLowerCase();
  if (trimmed.startsWith('<!doctype') || trimmed.startsWith('<html')) {
    return `CRM dashboard endpoint returned HTML from ${response.url}. Check API backend URL.`;
  }

  return body.slice(0, 200) || `CRM dashboard request failed (${response.status})`;
};

const quickActions = [
{ name: 'New Lead', href: '/crm/leads', icon: UserPlus },
{ name: 'Log Activity', href: '/crm/activities', icon: Calendar },
{ name: 'Create Quote', href: '/crm/quotes', icon: FileText },
{ name: 'View Pipeline', href: '/crm/pipeline', icon: Target }];


const FUNNEL_COLORS = ['#f97316', '#eab308', '#14b8a6', '#3b82f6', '#22c55e'];

export const CRMDashboard = () => {const { t } = useTranslation("crm");
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/crm/dashboard`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error(await readDashboardError(response));
      }
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(await readDashboardError(response));
      }
      const data = await response.json();
      setDashboardData(data);
      setError(null);
    } catch (err) {
      const message = err.message || 'Failed to load CRM dashboard';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500" />
      </div>);

  }

  const stats = dashboardData?.stats || {};
  const pipelineStages = dashboardData?.pipeline_stages || [];
  const activities = dashboardData?.activities || {};
  const recentContacts = dashboardData?.recent_contacts || [];
  const recentActivities = dashboardData?.recent_activities || [];

  return (
    <div className="space-y-6" data-testid="crm-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("crmOverview", "CRM Overview")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("trackLeadsOpportunities", "Track leads, opportunities, and customer relationships")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchDashboard} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Link to="/crm/reports">
            <Button variant="outline" className="gap-2">
              <TrendingUp className="h-4 w-4" />{t("salesReports", "Sales Reports")}

            </Button>
          </Link>
        </div>
      </div>

      {error &&
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <p className="text-yellow-500 text-sm">
            {dashboardData ? t("usingCachedData", "Using cached data - ") : t("dashboardDataUnavailable", "Dashboard data unavailable - ")}
            {error}
          </p>
        </div>
      }

      {/* KPI Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <CRMStatCard
          title="Total Contacts"
          value={stats.total_contacts || 0}
          icon={Users}
          color="teal" />
        
        <CRMStatCard
          title="Active Leads"
          value={stats.active_leads || 0}
          icon={Target}
          color="orange" />
        
        <CRMStatCard
          title="Pipeline Value"
          value={stats.pipeline_value || 0}
          icon={Handshake}
          color="blue"
          prefix="$" />
        
        <CRMStatCard
          title="Conversion Rate"
          value={stats.conversion_rate || 0}
          icon={DollarSign}
          color="green"
          suffix="%" />
        
      </div>

      {/* Quick Actions */}
      <Card className="card-elevated">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">{t("quickActions", "Quick Actions")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {quickActions.map((action) =>
            <Link key={action.name} to={action.href}>
                <Button
                variant="outline"
                className="w-full h-auto py-4 flex flex-col gap-2 hover:bg-teal-50 hover:border-teal-300 dark:hover:bg-teal-950/20 transition-colors">
                
                  <action.icon className="h-6 w-6 text-teal-600" />
                  <span className="text-sm font-medium">{action.name}</span>
                </Button>
              </Link>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Sales Funnel */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-muted-foreground" />{t("salesFunnel", "Sales Funnel")}

            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pipelineStages.map((stage, index) =>
              <div key={stage.stage} className="relative">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{stage.stage}</span>
                    <span className="text-sm text-muted-foreground">{stage.count}{t("leads", "leads \u2022 $")}{(stage.value / 1000).toFixed(0)}k</span>
                  </div>
                  <div className="h-8 rounded-lg overflow-hidden bg-muted">
                    <div
                    className="h-full rounded-lg transition-all"
                    style={{
                      width: `${pipelineStages[0]?.count ? stage.count / pipelineStages[0].count * 100 : 0}%`,
                      backgroundColor: FUNNEL_COLORS[index % FUNNEL_COLORS.length]
                    }} />
                  
                  </div>
                </div>
              )}
              {pipelineStages.length === 0 &&
              <div className="text-center py-8 text-muted-foreground">
                  <Target className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>{t("noPipelineData", "No pipeline data available")}</p>
                </div>
              }
            </div>
          </CardContent>
        </Card>

        {/* Activity Summary */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-muted-foreground" />{t("activitySummary", "Activity Summary")}

            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 rounded-lg bg-red-500/10">
                <p className="text-3xl font-bold text-red-500">{activities.overdue || 0}</p>
                <p className="text-sm text-muted-foreground">{t("overdue", "Overdue")}</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-amber-500/10">
                <p className="text-3xl font-bold text-amber-500">{activities.due_today || 0}</p>
                <p className="text-sm text-muted-foreground">{t("dueToday", "Due Today")}</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-blue-500/10">
                <p className="text-3xl font-bold text-blue-500">{activities.upcoming || 0}</p>
                <p className="text-sm text-muted-foreground">{t("upcoming", "Upcoming")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Upcoming Activities */}
        <Card className="card-elevated">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-muted-foreground" />{t("upcomingActivities", "Upcoming Activities")}

            </CardTitle>
            <Link to="/crm/activities">
              <Button variant="ghost" size="sm" className="text-muted-foreground">{t("viewAll", "View All")}

                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="space-y-3">
            {recentActivities.length > 0 ? recentActivities.map((activity) =>
            <div key={activity.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                <div className={`p-2 rounded-lg ${
              activity.type === 'call' ? 'bg-teal-100 dark:bg-teal-900/50' :
              activity.type === 'meeting' ? 'bg-orange-100 dark:bg-orange-900/50' :
              activity.type === 'email' ? 'bg-blue-100 dark:bg-blue-900/50' :
              'bg-purple-100 dark:bg-purple-900/50'}`
              }>
                  {activity.type === 'call' && <Phone className="h-4 w-4 text-teal-600" />}
                  {activity.type === 'meeting' && <Users className="h-4 w-4 text-orange-600" />}
                  {activity.type === 'email' && <Mail className="h-4 w-4 text-blue-600" />}
                  {activity.type === 'task' && <CheckSquare className="h-4 w-4 text-purple-600" />}
                  {activity.type === 'note' && <FileText className="h-4 w-4 text-gray-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{activity.subject}</p>
                  <p className="text-xs text-muted-foreground">{t("contact", "Contact #")}{activity.contact_id}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium">{new Date(activity.due_date).toLocaleDateString()}</p>
                </div>
              </div>
            ) :
            <div className="text-center py-8 text-muted-foreground">
                <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>{t("noUpcomingActivities", "No upcoming activities")}</p>
              </div>
            }
          </CardContent>
        </Card>

        {/* Recent Contacts */}
        <Card className="card-elevated">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-muted-foreground" />{t("recentContacts", "Recent Contacts")}

            </CardTitle>
            <Link to="/crm/accounts">
              <Button variant="ghost" size="sm" className="text-muted-foreground">{t("viewAll", "View All")}

                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="space-y-3">
            {recentContacts.length > 0 ? recentContacts.map((contact) =>
            <div key={contact.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-teal-500/10 flex items-center justify-center">
                    <span className="text-sm font-semibold text-teal-600">
                      {contact.first_name?.[0]}{contact.last_name?.[0]}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{contact.first_name} {contact.last_name}</p>
                    <p className="text-xs text-muted-foreground">{contact.company}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">{contact.contact_type}</p>
                </div>
              </div>
            ) :
            <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>{t("noRecentContacts", "No recent contacts")}</p>
              </div>
            }
          </CardContent>
        </Card>
      </div>
    </div>);

};
